import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { siteData } from "../mock";
import {
  DollarSign,
  Send,
  Smartphone,
  Copy,
  Check,
  ExternalLink,
  CalendarDays,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  CreditCard,
  ShieldCheck,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const iconMap = { DollarSign, Send, Smartphone };

export default function PackageCheckoutModal({ isOpen, onClose, item, onSessionCountChange }) {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [copiedId, setCopiedId] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [clientInfo, setClientInfo] = useState({ name: "", email: "", phone: "" });
  const [payMode, setPayMode] = useState("full"); // "full" | "plan"
  const calendlyRef = useRef(null);

  const requiredSessions = item?.requiredSessions || 1;
  const sessionLabel = item?.sessionLabel || "session";
  const allBooked = bookings.length >= requiredSessions;
  const eventTitle = item?.eventTitle || "1 HOUR TRAINING SESSION";
  const eventDuration = item?.eventDuration || "1 hour";
  const detailsCollected = clientInfo.name.trim() && clientInfo.email.trim();

  // Reset flow whenever modal opens with a new item
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setBookings([]);
      setPayMode("full");
      // Keep clientInfo across the same session (loaded from localStorage below)
    }
  }, [isOpen, item?.id]);

  // Load remembered client details from localStorage so they only enter them once ever
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("tdf_client") || "null");
      if (saved && saved.name && saved.email) {
        setClientInfo({ name: saved.name, email: saved.email, phone: saved.phone || "" });
      }
    } catch (_) {}
  }, []);

  const saveClientLocally = (info) => {
    try {
      localStorage.setItem("tdf_client", JSON.stringify(info));
    } catch (_) {}
  };

  // Load Calendly script
  useEffect(() => {
    if (document.querySelector('script[src*="calendly.com/assets/external/widget.js"]')) return;
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  // Explicitly (re)initialize the Calendly widget every time we land on Step 2
  useEffect(() => {
    if (!isOpen || step !== 2 || allBooked) return;
    const node = calendlyRef.current;
    if (!node) return;

    let cancelled = false;
    const initWidget = () => {
      if (cancelled) return;
      if (!window.Calendly) return false;
      node.innerHTML = "";
      window.Calendly.initInlineWidget({
        url: `${item?.calendlyUrl || "https://calendly.com/thedealfitness/30min"}?hide_gdpr_banner=1&hide_event_type_details=1&hide_landing_page_details=1&background_color=0f0f0f&text_color=ffffff&primary_color=e10600`,
        parentElement: node,
        prefill: detailsCollected
          ? {
              name: clientInfo.name,
              email: clientInfo.email,
              firstName: clientInfo.name.split(" ")[0] || clientInfo.name,
              lastName: clientInfo.name.split(" ").slice(1).join(" ") || "",
              customAnswers: clientInfo.phone ? { a1: clientInfo.phone } : {},
            }
          : {},
      });
      return true;
    };

    if (!initWidget()) {
      const iv = setInterval(() => {
        if (initWidget()) clearInterval(iv);
      }, 200);
      return () => {
        cancelled = true;
        clearInterval(iv);
      };
    }
    return () => { cancelled = true; };
  }, [isOpen, step, allBooked, bookings.length, detailsCollected, clientInfo.name, clientInfo.email, clientInfo.phone]);

  // Listen for Calendly booking confirmations — collect every one and auto-advance to payment
  useEffect(() => {
    const onMessage = (e) => {
      if (!e.data || typeof e.data !== "object") return;
      if (e.data.event === "calendly.event_scheduled") {
        setBookings((prev) => {
          if (prev.length >= requiredSessions) return prev; // already at limit
          const next = [...prev, { at: new Date().toISOString(), payload: e.data.payload || null }];
          toast({
            title: "Date locked in!",
            description: "Continuing to payment…",
          });
          // Auto-advance to Step 3 after a short delay so the user sees the confirmation
          if (next.length >= requiredSessions) {
            setTimeout(() => setStep(3), 900);
          }
          return next;
        });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [requiredSessions, sessionLabel, toast]);

  if (!item) return null;

  const handleCopy = async (method) => {
    try {
      await navigator.clipboard.writeText(method.handle);
      setCopiedId(method.id);
      toast({ title: `${method.name} handle copied`, description: method.handle });
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast({ title: "Copy failed", description: "Please copy manually." });
    }
  };

  const memoText = `${item.title} — [Your Name]`;

  const StepDot = ({ n, label }) => (
    <div className="flex items-center gap-2">
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold font-heading transition-colors ${
          step >= n
            ? "bg-[#e10600] text-white"
            : "bg-white/10 text-white/60"
        }`}
      >
        {step > n ? <Check size={13} /> : n}
      </div>
      <span
        className={`hidden sm:inline text-[11px] tracking-[0.2em] font-heading font-semibold ${
          step >= n ? "text-white" : "text-white/40"
        }`}
      >
        {label}
      </span>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl bg-[#0f0f0f] border border-[#e10600]/25 text-white p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        {/* Header + stepper */}
        <div className="bg-gradient-to-b from-[#e10600]/15 to-transparent px-6 md:px-8 pt-7 pb-5 border-b border-white/5">
          <div className="flex items-center gap-3 mb-4">
            <StepDot n={1} label="REVIEW" />
            <div className={`h-px flex-1 ${step >= 2 ? "bg-[#e10600]" : "bg-white/10"}`} />
            <StepDot n={2} label="PICK DATE" />
            <div className={`h-px flex-1 ${step >= 3 ? "bg-[#e10600]" : "bg-white/10"}`} />
            <StepDot n={3} label="PAY" />
          </div>

          <DialogHeader className="text-left">
            <DialogTitle className="font-display font-bold text-white text-2xl md:text-3xl tracking-wide leading-tight">
              {item.title}
            </DialogTitle>
            <DialogDescription className="text-white/70 text-sm md:text-base mt-1">
              {item.subtitle}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex items-baseline gap-3 flex-wrap">
            <span className="font-display font-bold text-white text-4xl md:text-5xl">
              ${item.price.toLocaleString()}
            </span>
            {item.priceNote && (
              <span className="text-white/60 text-sm">{item.priceNote}</span>
            )}
            {item.savingsNote && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-heading font-bold tracking-[0.15em] bg-emerald-500/15 text-emerald-400 border border-emerald-500/35">
                {item.savingsNote}
              </span>
            )}
          </div>
        </div>

        {/* STEP 1 — Review */}
        {step === 1 && (
          <div className="px-6 md:px-8 py-7">
            <p className="deal-yellow font-heading tracking-[0.3em] text-xs mb-3">
              WHAT HAPPENS NEXT
            </p>
            <ol className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#e10600] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                <p className="text-white/85">
                  Pick your <span className="font-semibold text-white">first {sessionLabel} date</span> on the calendar (next step)
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#e10600] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                <p className="text-white/85">
                  Pay <span className="font-semibold text-white">in full</span> for the whole package
                  {item.totalSessions > 1 && (
                    <> — Chris helps you schedule the remaining {item.totalSessions - 1} sessions after payment</>
                  )}
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#e10600] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
                <p className="text-white/85">
                  Chris confirms your booking and you're locked in — let's train!
                </p>
              </li>
            </ol>

            {item.upsell && (
              <div data-testid="checkout-upsell-banner" className="rounded-xl border border-[#e10600]/50 bg-[#e10600]/10 p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <p className="text-white/90 text-sm">
                  <span className="font-semibold text-white">Save ${item.upsell.savePerSession}/session.</span>{" "}
                  Book {item.upsell.sessions} for ${item.upsell.packageTotal} instead of ${item.upsell.dropInTotal}.
                </p>
                <button
                  data-testid="checkout-upsell-switch-btn"
                  onClick={item.upsell.onSwitch}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[#e10600] hover:bg-[#ff1a1a] text-white font-heading font-bold tracking-[0.2em] text-xs transition-colors shrink-0"
                >
                  SWITCH TO PACKAGE
                  <ArrowRight size={14} />
                </button>
              </div>
            )}

            {/* Session count picker — only shown for tier-based packages */}
            {item.tier && (
              <div className="rounded-xl border border-[#e10600]/30 bg-[#e10600]/5 p-5 mb-6">
                <p className="deal-yellow font-heading tracking-[0.3em] text-xs mb-3">
                  HOW MANY SESSIONS?
                </p>
                <div className="flex items-center justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      const next = Math.max(item.tier.min, item.totalSessions - 1);
                      onSessionCountChange?.(next);
                    }}
                    className="w-11 h-11 rounded-md bg-white/8 hover:bg-white/15 text-white font-bold text-xl transition-colors flex items-center justify-center"
                    aria-label="Decrease sessions"
                    disabled={item.totalSessions <= item.tier.min}
                  >
                    −
                  </button>
                  <div className="flex-1 text-center">
                    <div className="font-display font-bold text-white text-5xl leading-none">
                      {item.totalSessions}
                    </div>
                    <p className="text-white/60 text-xs tracking-[0.2em] font-heading mt-1">
                      SESSIONS · ${item.tier.hourlyRate}/HR
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const next = Math.min(item.tier.max, item.totalSessions + 1);
                      onSessionCountChange?.(next);
                    }}
                    className="w-11 h-11 rounded-md bg-white/8 hover:bg-white/15 text-white font-bold text-xl transition-colors flex items-center justify-center"
                    aria-label="Increase sessions"
                    disabled={item.totalSessions >= item.tier.max}
                  >
                    +
                  </button>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                  <span className="text-white/60 text-sm font-heading tracking-[0.15em]">
                    TOTAL
                  </span>
                  <span className="font-display font-bold text-white text-3xl">
                    ${item.price.toLocaleString()}
                  </span>
                </div>
                <p className="text-white/50 text-xs mt-2">
                  Choose anywhere from {item.tier.min} to {item.tier.max} sessions in this tier.
                </p>
              </div>
            )}

            {/* Client details — collected once, prefilled on every Calendly booking */}
            <div className="rounded-xl border border-white/10 bg-white/5 p-5 mb-6">
              <p className="deal-yellow font-heading tracking-[0.3em] text-xs mb-1">
                YOUR DETAILS
              </p>
              <p className="text-white/60 text-sm mb-4">
                Enter once — we'll auto-fill every booking on the next step.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  value={clientInfo.name}
                  onChange={(e) => setClientInfo({ ...clientInfo, name: e.target.value })}
                  placeholder="Full name *"
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#e10600] transition-colors text-sm"
                />
                <input
                  type="email"
                  value={clientInfo.email}
                  onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                  placeholder="Email *"
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#e10600] transition-colors text-sm"
                />
                <input
                  value={clientInfo.phone}
                  onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                  placeholder="Phone (optional)"
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#e10600] transition-colors text-sm md:col-span-2"
                />
              </div>
            </div>

            <button
              onClick={() => {
                if (!detailsCollected) {
                  toast({
                    title: "Add your name and email",
                    description: "So Chris knows who's booking.",
                  });
                  return;
                }
                saveClientLocally(clientInfo);
                setStep(2);
              }}
              className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md font-heading font-bold tracking-[0.2em] text-sm transition-colors ${
                detailsCollected
                  ? "bg-[#e10600] hover:bg-[#ff1a1a] text-white"
                  : "bg-white/10 text-white/40 cursor-not-allowed"
              }`}
            >
              <CalendarDays size={16} />
              {requiredSessions > 1 ? `BOOK ALL ${requiredSessions} DATES` : "PICK YOUR DATE"}
              <ArrowRight size={16} />
            </button>

            {/* Trust + privacy note */}
            <div className="mt-5 pt-5 border-t border-white/8">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <ShieldCheck size={14} className="text-emerald-400" />
                <p className="text-white/70 text-[11px] tracking-[0.15em] font-heading font-semibold">
                  SSL SECURED · YOUR INFO IS PRIVATE
                </p>
              </div>
              <p className="text-white/50 text-xs leading-relaxed">
                We only use your details to schedule sessions and process payment. See our{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#e10600] hover:underline font-semibold"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </div>
        )}

        {/* STEP 2 — Calendar */}
        {step === 2 && (
          <div className="px-6 md:px-8 py-6">
            <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
              <div>
                <p className="deal-yellow font-heading tracking-[0.3em] text-xs mb-1">
                  STEP 2 · {requiredSessions > 1 ? `BOOK ALL ${requiredSessions} DATES` : "CHOOSE YOUR DATE"}
                </p>
                <p className="text-white/70 text-sm">
                  {requiredSessions > 1
                    ? `Schedule every ${sessionLabel} below. Payment unlocks once all ${requiredSessions} are booked.`
                    : `Pick your date below. Payment unlocks once your ${sessionLabel} is confirmed.`}
                </p>
              </div>

              {/* Progress badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${
                allBooked
                  ? "bg-emerald-500/12 border-emerald-500/40 text-emerald-400"
                  : "bg-[#e10600]/10 border-[#e10600]/40 text-[#e10600]"
              }`}>
                <CalendarDays size={16} />
                <span className="font-heading font-bold tracking-[0.15em] text-xs">
                  {bookings.length} / {requiredSessions} BOOKED
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full h-2 bg-white/8 rounded-full overflow-hidden mb-5">
              <div
                className={`h-full transition-all duration-500 ${allBooked ? "bg-emerald-500" : "bg-[#e10600]"}`}
                style={{ width: `${Math.min(100, (bookings.length / requiredSessions) * 100)}%` }}
              />
            </div>

            {allBooked ? (
              <div className="rounded-xl bg-emerald-500/8 border border-emerald-500/30 p-6 flex items-start gap-4">
                <CheckCircle2 size={28} className="text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <p className="deal-yellow font-heading tracking-[0.3em] text-xs mb-1">
                    ALL DATES CONFIRMED
                  </p>
                  <p className="text-white text-lg font-semibold">
                    {requiredSessions > 1
                      ? `All ${requiredSessions} ${sessionLabel}s are on the calendar.`
                      : `Your ${sessionLabel} is on the calendar.`}
                  </p>
                  <p className="text-white/70 text-sm mt-2">
                    Continue to payment to finalize your{" "}
                    <span className="text-white font-semibold">{item.title.toLowerCase()}</span>.
                    Chris will confirm as soon as payment lands.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Custom event header (replaces Calendly's hidden panel) */}
                <div className="rounded-t-xl border border-b-0 border-white/10 bg-[#141414] px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#e10600]" />
                    <p className="font-heading font-bold text-white text-sm tracking-[0.15em]">
                      {eventTitle}
                    </p>
                  </div>
                  <p className="text-white/60 text-xs tracking-[0.15em]">
                    DURATION · {eventDuration.toUpperCase()}
                  </p>
                  {detailsCollected && (
                    <p className="text-emerald-400 text-xs tracking-[0.15em] ml-auto">
                      ✓ DETAILS AUTO-FILLED FOR {clientInfo.name.split(" ")[0].toUpperCase()}
                    </p>
                  )}
                </div>

                <div className="rounded-b-xl overflow-hidden border border-white/10 bg-[#0f0f0f]">
                  <div
                    ref={calendlyRef}
                    className="calendly-inline-widget"
                    style={{ minWidth: "320px", width: "100%", height: "680px" }}
                  />
                </div>
              </>
            )}

            {!allBooked && bookings.length > 0 && (
              <div className="mt-4 rounded-lg border border-[#e10600]/25 bg-[#e10600]/5 px-4 py-3 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#e10600] animate-soft-pulse" />
                <p className="text-white/85 text-sm">
                  <span className="font-semibold text-white">{requiredSessions - bookings.length}</span>{" "}
                  more {sessionLabel}{(requiredSessions - bookings.length) > 1 ? "s" : ""} to book — pick another date on the calendar above.
                </p>
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setStep(1)}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-white/8 hover:bg-white/12 text-white font-heading font-bold tracking-[0.2em] text-xs transition-colors"
              >
                <ArrowLeft size={14} />
                BACK
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!allBooked}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-heading font-bold tracking-[0.2em] text-sm transition-colors ${
                  allBooked
                    ? "bg-[#e10600] hover:bg-[#ff1a1a] text-white"
                    : "bg-white/10 text-white/40 cursor-not-allowed"
                }`}
              >
                {allBooked
                  ? "CONTINUE TO PAYMENT"
                  : `BOOK ${requiredSessions - bookings.length} MORE TO CONTINUE`}
                {allBooked && <ArrowRight size={16} />}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — Payment */}
        {step === 3 && (
          <div className="px-6 md:px-8 py-6">
            {(() => {
              const plan = item.installmentPlan;
              const payingNow = plan && payMode === "plan" ? plan.amountEach : item.price;
              return (
                <>
                  <div className="rounded-xl bg-emerald-500/8 border border-emerald-500/30 p-4 mb-6 flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-emerald-400 shrink-0 mt-0.5" />
                    <div className="text-sm text-white/90">
                      <p className="font-semibold text-white mb-1">
                        Your first {sessionLabel} is on the calendar!
                      </p>
                      <p>
                        Pay in full below to lock in{" "}
                        {item.totalSessions > 1 ? (
                          <>
                            <span className="font-semibold text-white">all {item.totalSessions} sessions</span>. Chris will schedule the remaining {item.totalSessions - 1} with you after payment.
                          </>
                        ) : (
                          <>your booking.</>
                        )}{" "}
                        Amount due:{" "}
                        <span className="font-display font-bold text-white text-lg">
                          ${payingNow.toLocaleString()}
                        </span>
                        {plan && payMode === "plan" && (
                          <span className="text-white/60"> (payment 1 of {plan.count})</span>
                        )}
                        .
                      </p>
                    </div>
                  </div>

                  {/* Payment plan toggle (only shows if the package has an installment plan) */}
                  {plan && (
                    <div className="mb-6">
                      <p className="deal-yellow font-heading tracking-[0.3em] text-xs mb-3">
                        CHOOSE HOW TO PAY
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setPayMode("full")}
                          className={`text-left rounded-xl p-4 border-2 transition-colors ${
                            payMode === "full"
                              ? "border-[#e10600] bg-[#e10600]/10"
                              : "border-white/10 bg-white/5 hover:border-white/25"
                          }`}
                        >
                          <p className="font-heading font-bold text-white text-sm tracking-[0.15em] mb-1">
                            PAY IN FULL
                          </p>
                          <p className="font-display font-bold text-white text-2xl mb-0.5">
                            ${item.price.toLocaleString()}
                          </p>
                          <p className="text-white/60 text-xs">One-time payment</p>
                        </button>

                        <button
                          type="button"
                          onClick={() => setPayMode("plan")}
                          className={`text-left rounded-xl p-4 border-2 transition-colors ${
                            payMode === "plan"
                              ? "border-[#e10600] bg-[#e10600]/10"
                              : "border-white/10 bg-white/5 hover:border-white/25"
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-heading font-bold text-white text-sm tracking-[0.15em]">
                              {plan.count}-PAYMENT PLAN
                            </p>
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[9px] font-heading font-bold tracking-[0.15em]">
                              EASIER
                            </span>
                          </div>
                          <p className="font-display font-bold text-white text-2xl mb-0.5">
                            ${plan.amountEach.toLocaleString()}
                            <span className="text-white/60 text-xs font-heading font-normal">
                              {" "}/ {plan.cadence}
                            </span>
                          </p>
                          <p className="text-white/60 text-xs">
                            Pay today, ${plan.amountEach.toLocaleString()} each {plan.cadence.replace(/ly$/, "")} for {plan.count - 1} more months
                          </p>
                        </button>
                      </div>
                      {payMode === "plan" && (
                        <p className="mt-3 text-white/60 text-xs leading-relaxed">
                          Send <span className="text-white font-semibold">${plan.amountEach.toLocaleString()}</span>{" "}
                          today via any method below. Chris will bill or remind you for the remaining{" "}
                          {plan.count - 1} payment{plan.count - 1 > 1 ? "s" : ""} on the same day each month.
                        </p>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {/* PRIMARY — Card payment via Square */}
            {(() => {
              const cfg = siteData.cardPayments || {};
              const plan = item.installmentPlan;
              const isPlan = Boolean(plan && payMode === "plan");
              // Prefer subscription URL for plan mode; fall back to normal card URL
              const subUrl = cfg.subscriptionsById?.[item.id] || "";
              const stdUrl = cfg.byId?.[item.id] || cfg.default || "";
              const cardUrl = isPlan && subUrl ? subUrl : stdUrl;
              const cardReady = Boolean(cardUrl);
              const dueNow = isPlan ? plan.amountEach : item.price;
              const acceptedMethods = cfg.acceptedMethods || ["Credit Card", "Apple Pay", "Google Pay"];
              return (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <p className="deal-yellow font-heading tracking-[0.3em] text-xs">
                      RECOMMENDED — PAY BY CARD
                    </p>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/12 text-emerald-400 text-[10px] font-heading font-bold tracking-[0.15em]">
                      <ShieldCheck size={10} />
                      SECURE
                    </span>
                    {isPlan && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#e10600]/15 text-[#e10600] text-[10px] font-heading font-bold tracking-[0.15em]">
                        AUTO-RECURRING
                      </span>
                    )}
                  </div>
                  <a
                    href={cardReady ? cardUrl : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!cardReady) {
                        e.preventDefault();
                        toast({
                          title: isPlan
                            ? "Subscription not set up yet"
                            : "Card payment not set up yet",
                          description: "Ask Chris — for now use Cash App, Venmo or Zelle below.",
                        });
                      }
                    }}
                    className={`group block rounded-xl p-5 border transition-all ${
                      cardReady
                        ? "bg-gradient-to-r from-[#e10600]/95 to-[#c40500] border-[#e10600] hover:shadow-[0_10px_40px_-10px_rgba(225,6,0,0.6)]"
                        : "bg-[#141414] border-white/10 opacity-70 cursor-not-allowed"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-lg bg-white flex items-center justify-center shrink-0">
                        <CreditCard size={26} className="text-[#e10600]" strokeWidth={2.4} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white/70 text-[10px] tracking-[0.25em] font-heading mb-0.5">
                          POWERED BY SQUARE {isPlan ? "· AUTO-BILLED MONTHLY" : ""}
                        </p>
                        <p className="text-white font-display font-bold text-xl md:text-2xl leading-tight">
                          PAY ${dueNow.toLocaleString()} SECURELY
                          {isPlan && (
                            <span className="text-white/80 text-sm font-heading font-normal">
                              {" "}· 1 of {plan.count}
                            </span>
                          )}
                        </p>
                        <p className="text-white/80 text-xs mt-1">
                          {cardReady
                            ? isPlan
                              ? `Enroll once — Square auto-charges ${plan.count - 1} more monthly payments`
                              : "Instant confirmation"
                            : "Payment link not configured yet"}
                        </p>
                      </div>
                      <ExternalLink
                        size={20}
                        className="text-white/90 shrink-0 group-hover:translate-x-0.5 transition-transform hidden sm:block"
                      />
                    </div>
                  </a>

                  {/* Payment method badges — like Jotform: Credit Card · Apple Pay · Google Pay */}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-white/50 text-[10px] tracking-[0.25em] font-heading font-semibold uppercase">
                      Accepts
                    </span>
                    {acceptedMethods.map((m) => (
                      <span
                        key={m}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/12 bg-white/5 text-white/85 text-[11px] font-heading font-semibold tracking-[0.1em]"
                      >
                        {m === "Apple Pay" && (
                          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
                            <path d="M17.5 12.3c0-2 1.7-3 1.7-3-.9-1.3-2.4-1.5-3-1.5-1.3-.1-2.5.7-3.1.7-.7 0-1.6-.7-2.7-.7-1.4 0-2.7.8-3.4 2-1.4 2.5-.4 6.2 1 8.2.7 1 1.5 2.1 2.5 2 1 0 1.4-.6 2.7-.6 1.2 0 1.6.6 2.7.6 1.1 0 1.8-1 2.5-2 .5-.7.9-1.5 1.1-2.4-2-1-2-2.9-2-3.3zm-3-6c.6-.7 1-1.7 1-2.7-1 0-2 .6-2.7 1.3-.6.6-1.1 1.6-1 2.6 1 .1 2-.5 2.7-1.2z" />
                          </svg>
                        )}
                        {m === "Google Pay" && (
                          <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.6" />
                            <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
                          </svg>
                        )}
                        {m === "Credit Card" && (
                          <CreditCard size={12} strokeWidth={2.4} />
                        )}
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-white/10" />
              <p className="text-white/45 text-[10px] tracking-[0.3em] font-heading">
                OR SEND DIRECTLY
              </p>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <div className="space-y-3">
              {siteData.paymentMethods.map((m) => {
                const Icon = iconMap[m.icon];
                const isCopied = copiedId === m.id;
                return (
                  <div
                    key={m.id}
                    className="flex items-center gap-3 bg-[#141414] border border-white/8 rounded-xl p-4"
                  >
                    <div
                      className="w-11 h-11 rounded-md flex items-center justify-center shrink-0"
                      style={{ backgroundColor: m.accent }}
                    >
                      {Icon && <Icon size={20} className="text-white" strokeWidth={2.5} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/55 text-[10px] tracking-[0.25em] font-heading mb-0.5">
                        {m.name.toUpperCase()}
                      </p>
                      <p className="text-white font-medium truncate">{m.handle}</p>
                    </div>
                    <button
                      onClick={() => handleCopy(m)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#e10600] hover:bg-[#ff1a1a] text-white font-heading font-bold tracking-[0.15em] text-[11px] transition-colors"
                      aria-label={`Copy ${m.name} handle`}
                    >
                      {isCopied ? <Check size={13} /> : <Copy size={13} />}
                      {isCopied ? "COPIED" : "COPY"}
                    </button>
                    <a
                      href={m.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/8 hover:bg-white/12 text-white font-heading font-bold tracking-[0.15em] text-[11px] transition-colors"
                      aria-label={`Open ${m.name}`}
                    >
                      <ExternalLink size={13} />
                      OPEN
                    </a>
                  </div>
                );
              })}
            </div>

            {/* Memo helper */}
            <div className="mt-5 rounded-xl border border-[#e10600]/25 bg-[#e10600]/8 p-4">
              <p className="text-white/85 text-sm leading-relaxed">
                <span className="deal-yellow font-heading tracking-[0.15em] font-bold text-xs">
                  PAYMENT MEMO:
                </span>{" "}
                Include your <span className="font-semibold text-white">full name</span> and this reference:
              </p>
              <p className="mt-2 font-mono text-[#e10600] text-sm bg-black/40 px-3 py-2 rounded-md break-words">
                {memoText}
              </p>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setStep(2)}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-white/8 hover:bg-white/12 text-white font-heading font-bold tracking-[0.2em] text-xs transition-colors"
              >
                <ArrowLeft size={14} />
                BACK
              </button>
              <button
                onClick={async () => {
                  const plan = item.installmentPlan;
                  const dueNow = plan && payMode === "plan" ? plan.amountEach : item.price;
                  const paymentMode =
                    plan && payMode === "plan"
                      ? `${plan.count}-payment plan · $${plan.amountEach.toLocaleString()}/${plan.cadence} (paid 1 of ${plan.count})`
                      : "Paid in full";
                  // Fire-and-forget booking notification email to Chris
                  try {
                    const cfg = siteData.notify;
                    if (cfg?.endpoint) {
                      const times = bookings
                        .map((b, i) => `${i + 1}. ${b.at}`)
                        .join("\n");
                      await fetch(cfg.endpoint, {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Accept: "application/json",
                        },
                        body: JSON.stringify({
                          _subject: `New booking: ${item.title} — ${clientInfo.name}`,
                          _template: "table",
                          _captcha: "false",
                          client_name: clientInfo.name,
                          client_email: clientInfo.email,
                          client_phone: clientInfo.phone || "—",
                          package: item.title,
                          total_price: `$${item.price.toLocaleString()}`,
                          payment_mode: paymentMode,
                          due_now: `$${dueNow.toLocaleString()}`,
                          sessions_booked: `${bookings.length} / ${requiredSessions}`,
                          booking_times: times || "n/a",
                          payment_status: "Client indicated payment sent (verify in Square/Cash App/Venmo/Zelle)",
                          submitted_at: new Date().toLocaleString(),
                        }),
                      });
                    }
                  } catch (err) {
                    console.warn("Notification email failed:", err);
                  }
                  toast({
                    title: "Booking complete!",
                    description: "Chris has been notified and will confirm shortly.",
                  });
                  onClose();
                }}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white font-heading font-bold tracking-[0.2em] text-sm transition-colors"
              >
                <CheckCircle2 size={16} />
                I'VE SENT PAYMENT — DONE
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
